<script setup lang="ts">
import { computed } from 'vue'
import AuthShell from './AuthShell.vue'
import AuthSpinner from './AuthSpinner.vue'
import { AUTH_FOOTER_LINK, AUTH_LINK } from './authClasses'
import {
  LOCKED_OUT_ASIDE,
  lockedOutBody,
  ownerMailto,
  retryNoticeText,
  type LockoutOwner,
  type LockoutReason,
  type RetryNotice,
} from './lockedOutModel'
import { initialsOf } from '@/lib/personName'

/**
 * "Your access is paused" (LockedOut board): the one screen behind both the `/locked-out` route
 * and the App.vue overlay that blocks the app while the subscription locks this person out (R6).
 *
 * - `reason`: `pending` while the owner is still choosing who stays, `downgraded` when the free
 *   plan's list left this person off.
 * - `owner`: who can restore access (`GET /auth/lockout-info`). Leave it undefined while that is
 *   loading (a placeholder card shows); null when there is none or it couldn't be loaded ("Ask your
 *   organization owner", no card, no "Email the owner").
 * - `orgName`, `freeSeats`, `message`: fill the paragraph (see `lockedOutBody`).
 * - `email`: "Signed in as …" in the footer.
 * - `signingOut` / `retrying`: busy states of Sign out / Try again. `retryNotice`: what the last
 *   Try again found, announced under the note — `still-locked` (the API still refuses) or
 *   `failed` (the check itself failed, e.g. offline).
 * - Emits `signout` and `retry`; the host does the work (see composables/useLockedOut).
 */
const props = defineProps<{
  reason: LockoutReason
  message?: string | null | undefined
  email?: string | null | undefined
  orgName?: string | null | undefined
  owner?: LockoutOwner | null | undefined
  freeSeats?: number | null | undefined
  signingOut?: boolean | undefined
  retrying?: boolean | undefined
  retryNotice?: RetryNotice | null | undefined
}>()

const emit = defineEmits<{ signout: []; retry: [] }>()

const body = computed(() =>
  lockedOutBody({ reason: props.reason, orgName: props.orgName, freeSeats: props.freeSeats, message: props.message }),
)
const mailto = computed(() => ownerMailto(props.owner, props.orgName))
const initials = computed(() => initialsOf(props.owner?.name || props.owner?.email))
const notice = computed(() => (props.retrying ? '' : retryNoticeText(props.retryNotice)))
</script>

<template>
  <AuthShell :width="420" aside-label="What happens to your work">
    <div class="flex flex-col gap-5">
      <span class="grid h-12 w-12 place-items-center rounded-xl bg-warning-tint text-warning" aria-hidden="true">
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <rect x="4" y="11" width="16" height="10" rx="2" />
          <path d="M8 11V7a4 4 0 0 1 8 0v4" />
        </svg>
      </span>

      <div>
        <h1 class="m-0 text-[28px] font-semibold leading-[1.2] tracking-[-0.02em]">Your access is paused</h1>
        <p class="mb-0 mt-2.5 leading-[1.55] text-ink-2">{{ body }}</p>
      </div>

      <!-- Who can restore you -->
      <div v-if="owner" class="flex items-center gap-3 rounded-xl border border-line bg-surface px-4 py-3.5">
        <span
          class="grid h-9 w-9 flex-none place-items-center rounded-full bg-accent-tint text-[12.5px] font-semibold text-accent-hover"
          aria-hidden="true"
        >{{ initials }}</span>
        <span class="min-w-0 flex-1 leading-[1.35]">
          <span class="block text-[12.5px] text-ink-3">Only the owner can restore you</span>
          <span v-if="owner.name" class="block font-semibold">{{ owner.name }}</span>
          <span class="block text-[13px] text-ink-2 [overflow-wrap:anywhere]">{{ owner.email }}</span>
        </span>
      </div>
      <div
        v-else-if="owner === undefined"
        class="flex items-center gap-3 rounded-xl border border-line bg-surface px-4 py-3.5"
        aria-hidden="true"
      >
        <span class="h-9 w-9 flex-none animate-pulse rounded-full bg-wash" />
        <span class="flex flex-1 flex-col gap-2">
          <span class="h-3 w-40 max-w-full animate-pulse rounded bg-wash" />
          <span class="h-3 w-28 max-w-full animate-pulse rounded bg-wash" />
        </span>
      </div>
      <p v-else class="m-0 font-medium text-ink">Ask your organization owner to add you back to the active list.</p>

      <div class="flex flex-wrap gap-2">
        <a v-if="mailto" :href="mailto" class="btn-lg btn-primary flex-[1_1_180px] text-sm">Email the owner</a>
        <button
          type="button"
          class="btn-lg btn-secondary flex-[1_1_140px] text-sm font-medium"
          :disabled="signingOut"
          @click="emit('signout')"
        >
          <AuthSpinner v-if="signingOut" />
          {{ signingOut ? 'Signing out…' : 'Sign out' }}
        </button>
      </div>

      <div role="note" class="border-t border-line pt-4 text-[13px] leading-[1.5] text-ink-3">
        <span class="font-semibold text-ink">Seeing this while your owner is choosing who stays?</span>
        Access comes back as soon as they confirm the list.
        <button
          type="button"
          :class="[AUTH_LINK, 'inline-flex min-h-11 items-center gap-1.5 align-middle disabled:cursor-wait lg:min-h-0']"
          :disabled="retrying"
          :aria-busy="retrying"
          @click="emit('retry')"
        >
          <AuthSpinner v-if="retrying" class="!h-3.5 !w-3.5" />
          {{ retrying ? 'Checking…' : 'Try again' }}
        </button>
        <span role="status" :class="notice ? 'mt-2 block font-medium text-ink' : 'sr-only'">{{ notice }}</span>
      </div>
    </div>

    <template #footer>
      <span v-if="email">Signed in as <span class="[overflow-wrap:anywhere]">{{ email }}</span></span>
      <!-- In-app help is out of reach while the app is blocked: write to support instead. -->
      <a href="mailto:hello@nerddevs.com" :class="AUTH_FOOTER_LINK">Help</a>
    </template>

    <template #aside>
      <div class="flex flex-col gap-6">
        <p class="m-0 max-w-[500px] text-[30px] font-medium leading-[1.25] tracking-[-0.02em]">{{ LOCKED_OUT_ASIDE.title }}</p>
        <ul class="m-0 flex max-w-[500px] list-none flex-col gap-4 p-0">
          <li v-for="bullet in LOCKED_OUT_ASIDE.bullets" :key="bullet" class="grid grid-cols-[22px_minmax(0,1fr)] gap-3">
            <svg
              class="mt-0.5 text-accent-mark"
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              aria-hidden="true"
            >
              <path d="m5 12 5 5 9-10" />
            </svg>
            <span class="text-[15px] leading-[1.5] text-line">{{ bullet }}</span>
          </li>
        </ul>
      </div>
    </template>
  </AuthShell>
</template>
